import { WEBCAM_STREAM_NAME } from '@/entities/webrtc'

import { getParticipantShortCallIdFromTrackId, getTrackNameFromTrackId } from '@/shared/services/media-helper'

import { useCallSessionMediaStore } from '../store'

type BuildParticipantPlaybackStreamParams = {
  audioTrack?: MediaStreamTrack | undefined
  videoTrack?: MediaStreamTrack | undefined
}

const findLiveAudioTrack = (streams: MediaStream[] | undefined): MediaStreamTrack | undefined => {
  if (!streams?.length) return undefined
  for (const stream of streams) {
    const track = stream.getAudioTracks().find((t) => t.readyState !== 'ended')
    if (track) return track
  }
  return undefined
}

export const getParticipantAudioTrack = (
  sessionId: string,
  participantKey: string,
): MediaStreamTrack | undefined => {
  const { getStreams, getAudioStreamsInSession } = useCallSessionMediaStore()

  const byKey = findLiveAudioTrack(getStreams(sessionId, participantKey))
  if (byKey) return byKey

  // Fallback: key mismatch between IM shortId and store map — match by track id prefix.
  const sessionAudio = getAudioStreamsInSession(sessionId)
  if (!sessionAudio?.length) return undefined

  for (const stream of sessionAudio) {
    for (const track of stream.getAudioTracks()) {
      if (track.readyState === 'ended') continue
      if (getParticipantShortCallIdFromTrackId(track.id) === participantKey) {
        return track
      }
    }
  }

  return undefined
}

export const shouldAttachAudioToVideoTrack = (videoTrack?: MediaStreamTrack): boolean => {
  if (!videoTrack) return false
  return getTrackNameFromTrackId(videoTrack.id) === WEBCAM_STREAM_NAME
}

export const buildParticipantPlaybackStream = ({
  audioTrack,
  videoTrack,
}: BuildParticipantPlaybackStreamParams): MediaStream | undefined => {
  const tracks: MediaStreamTrack[] = []
  const seen = new Set<string>()

  const pushTrack = (track?: MediaStreamTrack) => {
    if (!track || track.readyState === 'ended') return
    if (seen.has(track.id)) return
    seen.add(track.id)
    tracks.push(track)
  }

  pushTrack(videoTrack)
  pushTrack(audioTrack)

  if (!tracks.length) return undefined
  return new MediaStream(tracks)
}
